<template>
  <div :class="{ 'no-workspaces': workspaces === null || workspaces.length === 0, 'workspaces-card': workspaces && workspaces.length > 0 }">
    <span v-if="workspaces === null">
      {{ $t('GlobalSearchWorkspacesInstruction') }}
    </span>

    <span v-else-if="workspaces.length === 0">
      {{ $t('NoResults') }}
    </span>

    <div v-else-if="workspaces.length > 0" class="table-container">
      <span class="table-title">{{ $t('WorkspacesList') }}</span>
      <Table
        customButton
        :style="{'width': '100%'}"
        :loading="loading"
        :headers="headers"
        :items="workspaces"
        :sortable="true"
        :scroll-id="scrollId"
        @onScrollBottom="onScrollBottom"
        @onScrollTop="onScrollTop"
        @sortClick="onSortClick"
        @onRowClick="selectedWorkspace = $event"
      >
        <template v-slot:customButton>
          <div class="button-container">
            <custom-button
              :label="$t('Go') + ' >'"
              :button-color="'var(--app-primary-base)'"
              :label-color="'#FFF'"
              :button-styling="buttonStyling"
              @buttonClick="openWorkspace()"
            />
          </div>
        </template>
      </Table>
    </div>

    <loading-spinner
      :active.sync="loading"
      :is-full-page="true"
      color="var(--app-primary-base)"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { bus } from '@/main.js';
import Table from "@/newComponents/_Molecules/Table.vue";
import CustomButton from "@/newComponents/_Atoms/CustomButton.vue";
import LoadingSpinner from "vue-loading-overlay";
import _ from "lodash";

export default {
  name: "GlobalSearchWorkspaces",

  components: {
    Table,
    LoadingSpinner,
    CustomButton,
  },

  data() {
    return {
      workspaces: null,
      headers: [
        { text: this.$i18n.tc("Label", 1), varName: "wp_label" },
        { text: this.$i18n.t("PlanName"), varName: "wp_plan_name" },
        { text: this.$i18n.tc("Phase", 1), varName: "wp_status" },
        { text: this.$i18n.t("Status"), varName: "wp_status_active" },
      ],

      pageNumber: 1,
      pagination: 10,
      currentPageTop: 1,
      currentPageBot: 1,
      lastPage: 2,
      sortOrder: "wp_label",

      loading: false,

      buttonStyling: {
        'font-size': '10px',
        'line-height': '12px',
        'height': '20px',
        'font-weight': '600',
        'border-radius': '4px',
        'padding': '6px 12px'
      },

      selectedResult: null,
      selectedWorkspace: null,
    }
  },

  mounted() {
    bus.$on('on-row-click', clickedObject => {
      this.selectedResult = clickedObject;
      this.getInitialObjectWorkspaces()
    })
  },

  destroyed() {
    bus.$off('on-row-click');
  },

  computed: {
    scrollId() {
      return 'scroll-bar-workspaces';
    },
  },

  methods: {
    ...mapActions([
      "fetchWorkspacesContainsObject",
    ]),

    async getInitialObjectWorkspaces() {
      this.pageNumber = 1;
      this.currentPageBot = 1;
      this.currentPageTop = 1;
      this.loading = true;
      try {
        let queryParams = {
          paginate: this.pagination,
          paginate: false,
        }

        if (this.sortOrder !== null) {
          queryParams["sort"] = this.sortOrder;
        }

        this.workspacesObject = await this.fetchWorkspacesContainsObject({
          objectType: this.selectedResult.type,
          objectUuid: this.selectedResult.uuid,
          params: queryParams,
        })

        this.workspaces = Object.values(this.workspacesObject);
      } catch (error) {
        console.error(error);
        this.workspaces = [];
      } finally {
        this.loading = false;
      }
    },

    onScrollBottom() {
      if (this.currentPageBot !== this.lastPage) {
        this.pageNumber++;
        this.getWorkspacesBottom();
      }
    },

    async getWorkspacesBottom() {
      this.loading = true;
      if (this.workspaces.length >= (3 * this.pagination)) {
        this.currentPageTop++;
      }
      this.currentPageBot++;

      try {
        let queryParams = {
          pageNumber: this.currentPageBot,
          paginate: this.pagination,
        };

        if (this.sortOrder !== null) {
          queryParams["sort"] = this.sortOrder;
        }

        let workspacesObject = await this.fetchWorkspacesContainsObject({
          objectType: this.selectedResult.type,
          objectUuid: this.selectedResult.uuid,
          params: queryParams,
        })

        this.workspaces = workspacesObject.data.data;
        this.lastPage = workspacesObject.data.meta.last_page;

        if (this.workspaces.length > (3 * this.pagination)) {
          this.workspaces = this.workspaces.slice(this.workspaces.length - (3 * this.pagination));
        }

        this.scrollTableBottom();
      } catch (error) {
        console.error(error);
        this.handleError(error);
        this.workspaces = [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * Scroll to the section of the table with the latest data
     */
     scrollTableBottom() {
      const currentScroll = document.getElementById(this.scrollId).scrollTop;
      this.loading = false;

      const rowElement = document.getElementById("rowId");
      if (rowElement) {
        const rowHeight = rowElement.clientHeight;
        for (let i = 0; i < this.pagination; i++) {
          const offset = rowHeight * i;
          document.getElementById(this.scrollId).scrollTo({
            top: currentScroll - offset,
            behavior: "smooth",
          });
        }
      }
    },

    onScrollTop() {
      if (this.currentPageTop !== 1) {
        this.pageNumber--;
        this.getWorkspacesTop();
      }
    },

    async getWorkspacesTop() {
      this.loading = true;
      this.currentPageTop--;

      try {
        let queryParams = {
          pageNumber: this.currentPageTop,
          paginate: this.pagination,
        };

        if (this.sortOrder !== null) {
          queryParams["sort"] = this.sortOrder;
        }

        let workspacesObject = await this.fetchWorkspacesContainsObject({
          objectType: this.selectedResult.type,
          objectUuid: this.selectedResult.uuid,
          params: queryParams,
        })

        this.workspaces = workspacesObject.data.data;
        this.lastPage = workspacesObject.data.meta.last_page;

        if (this.workspaces.length > (3 * this.pagination)) {
          let numberOfElementsToRemove = this.workspaces.length - (3 * this.pagination);
          this.workspaces.splice(this.workspaces.length - numberOfElementsToRemove, numberOfElementsToRemove);
          this.currentPageBot--;
        }

        this.scrollTableTop();
      } catch (error) {
        console.error(error);
        this.handleError(error);
        this.workspaces = [];
      } finally {
        this.loading = false;
      }

    },

    scrollTableTop() {
      const currentScroll = document.getElementById(this.scrollId).scrollTop;
      this.loading = false;

      const rowElement = document.getElementById("rowId");
      if (rowElement) {
        const rowHeight = rowElement.clientHeight;
        for (let i = 0; i < this.pagination; i++) {
          const offset = rowHeight * i;
          document.getElementById(this.scrollId).scrollTo({
            top: currentScroll + offset,
            behavior: "smooth",
          });
        }
      }
    },

    onSortClick(sortOrder, index) {
      if (sortOrder === "asc") {
        this.sortOrder = this.headers[index].varName;
      } else if (sortOrder === "desc") {
        this.sortOrder = `-${this.headers[index].varName}`;
      } else {
        this.sortOrder = sortOrder;
      }
      this.getInitialObjectWorkspaces();
    },

    openWorkspace: _.debounce(function () {
      this.$nextTick(() => {
        this.$router.replace({
          name: "home",
          params: {
            workspace_uuid: this.selectedWorkspace.wp_uuid,
            objectGeoJSON: this.selectedResult.geom
          },
        });
      })
    }, 50),
  },
}
</script>

<style scoped>
.workspaces-card,
.no-workspaces {
  height: 100%;
  max-height: 100%;
  background-color: #fff;
  position: relative;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
}

.no-workspaces {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.workspaces-card > span,
.no-workspaces > span {
  color: var(--app-primary-base);
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
}

.table-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: auto;
}

.table-title {
  color: #767676;
  font-family: Lato;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
  line-height: 20px;
  text-transform: uppercase;
  margin-bottom: 16px;
}

.button-container {
  display: flex;
  justify-content: flex-end;
}
</style>


// WEBPACK FOOTER //
// src/newComponents/WorkspacesManagement/GlobalSearch/GlobalSearchWorkspaces.vue